import React, { useState, useEffect } from 'react';

const KeyCapabilities = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const capabilities = [
    {
      id: 'design',
      title: 'Innovative Product Design & Development',
      icon: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z',
      points: [
        'Expert in-house design team specializing in sculpting, painting, and mold development',
        'Proprietary design, prototyping, and sampling process for trend-driven product creation',
        'Category-leading portfolio that meets evolving consumer preferences',
        'Industry leader in the statuary category with the highest revenue through innovative design'
      ]
    },
    {
      id: 'manufacturing',
      title: 'Precision Manufacturing & Cost Efficiency',
      icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4',
      points: [
        'BQLP (Better Quality, Lower Price) strategy ensures premium craftsmanship at competitive costs',
        'Strategic factory in China and Cambodia for cost efficiency & production flexibility',
        'Consistent business growth from 2009-2025 with a leading market position'
      ]
    },
    {
      id: 'sourcing',
      title: 'Strategic Sourcing & Supply Chain Agility',
      icon: 'M3 3h18v18H3V3z M8 7h8M8 12h8M8 17h8',
      points: [
        'End-to-end product development from sampling to final design execution',
        'Reliable supplier network & adaptive sourcing strategies that ensure product availability despite global trade shifts'
      ]
    },
    {
      id: 'growth',
      title: 'Expert & Growth',
      icon: 'M13 7h8m0 0v8m0-8l-8 8-4-4-6 6',
      points: [
        'Long-term partnerships with major North American retailers',
        'Efficient logistics and fulfillment for consistent product availability and performance tracking',
        'BSCI MONITORING ID: 25-0372288',
        '$1M+ in annual sales with premier retail partners'
      ],
      highlight: true
    }
  ];

  return (
    <section id="capabilities" className="bg-gray-50 py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Header */}
        <div className="text-center mb-12">
          <span className="text-sm font-semibold text-[#215f9a] uppercase tracking-wider">What We Do Best</span>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mt-2 mb-4">
            Key Capabilities
          </h1>
          <div className="w-16 h-1 bg-[#215f9a] mx-auto"></div>
          <p className="text-gray-600 text-lg mt-4 max-w-2xl mx-auto">
            Delivering excellence through innovation, precision, and strategic growth
          </p>
        </div>

        {/* Capabilities Grid */}
        <div className="grid md:grid-cols-2 gap-6">
          {capabilities.map((capability, idx) => (
            <div
              key={capability.id}
              className="transition-all duration-300"
            >
              <div 
                className={`h-full rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow ${
                  capability.highlight ? 'bg-gray-900 text-white' : 'bg-white'
                }`}
              >
                <div className="p-6">
                  {/* Icon Header */}
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-[#215f9a] flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={capability.icon} />
                      </svg>
                    </div>
                    <h2 className="text-lg md:text-xl font-bold leading-tight text-gray-900">
                      {capability.title}
                    </h2>
                  </div>

                  {/* Points List */}
                  <ul className="space-y-2">
                    {capability.points.map((point, pointIdx) => (
                      <li key={pointIdx} className="flex items-start gap-2">
                        <svg 
                          className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#215f9a]" 
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className={`text-sm leading-relaxed ${capability.highlight ? 'text-gray-300' : 'text-gray-600'}`}>
                          {point}
                        </span>
                      </li>
                    ))}
                  </ul>

                  {/* BSCI Badge for Growth section */}
                  {capability.id === 'growth' && (
                    <div className="mt-4 pt-3 border-t border-gray-700">
                      <span className="inline-block px-3 py-1 rounded-md bg-[#215f9a] text-white text-xs font-semibold">
                        BSCI CERTIFIED
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Stats Section */}
        <div className="mt-16">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-6 bg-white rounded-lg shadow-sm">
              <div className="text-3xl md:text-4xl font-bold text-[#215f9a] mb-1">
                2009
              </div>
              <p className="text-gray-500 text-sm">Year Established</p>
            </div>
            <div className="text-center p-6 bg-white rounded-lg shadow-sm">
              <div className="text-3xl md:text-4xl font-bold text-[#215f9a] mb-1">
                $1M+
              </div>
              <p className="text-gray-500 text-sm">Annual Sales</p>
            </div>
            <div className="text-center p-6 bg-white rounded-lg shadow-sm">
              <div className="text-3xl md:text-4xl font-bold text-[#215f9a] mb-1">
                500+
              </div>
              <p className="text-gray-500 text-sm">Products</p>
            </div>
            <div className="text-center p-6 bg-white rounded-lg shadow-sm">
              <div className="text-3xl md:text-4xl font-bold text-[#215f9a] mb-1">
                50+
              </div>
              <p className="text-gray-500 text-sm">Retail Partners</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default KeyCapabilities;